import {
	ArrowLeft,
	ArrowRight,
	BarChart3,
	BookmarkCheck,
	Circle,
	Clock3,
	Flower2,
	Heart,
	Image as ImageIcon,
	MessageCircle,
	Send,
	Shuffle,
	Trophy,
	Users,
	Zap,
	type LucideIcon,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type PointerEvent as ReactPointerEvent,
	type ReactNode,
} from "react";

type Screen =
	| "today"
	| "drawing"
	| "reveal"
	| "task"
	| "submit"
	| "evidence"
	| "journey"
	| "report"
	| "wall";

type Dimension = {
	key: string;
	name: string;
	shortName: string;
	count: number;
	color: string;
	soft: string;
	share: number;
};

type PermaModule = Dimension & {
	description: string;
	skill: string;
	exercises: string[];
	levels: Array<{
		name: string;
		items: string[];
	}>;
};

type DrawResult = {
	date: string;
	exercise: string;
	exerciseIndex: number;
	level: PermaModule["levels"][number];
	module: PermaModule;
	moduleIndex: number;
};

type NavigationMode = "push" | "replace" | "reset";
type SwipeState = {
	fromLeft: boolean;
	pointerId: number;
	startX: number;
	startY: number;
	tracking: boolean;
};

const dimensions: Dimension[] = [
	{
		key: "P",
		name: "积极情绪",
		shortName: "积极",
		count: 4,
		color: "var(--perma-p)",
		soft: "var(--perma-p-soft)",
		share: 32,
	},
	{
		key: "E",
		name: "投入心流",
		shortName: "投入",
		count: 2,
		color: "var(--perma-e)",
		soft: "var(--perma-e-soft)",
		share: 18,
	},
	{
		key: "R",
		name: "关系连接",
		shortName: "关系",
		count: 5,
		color: "var(--perma-r)",
		soft: "var(--perma-r-soft)",
		share: 21,
	},
	{
		key: "M",
		name: "意义感",
		shortName: "意义",
		count: 1,
		color: "var(--perma-m)",
		soft: "var(--perma-m-soft)",
		share: 11,
	},
	{
		key: "A",
		name: "成就感",
		shortName: "成就",
		count: 6,
		color: "var(--perma-a)",
		soft: "var(--perma-a-soft)",
		share: 18,
	},
];

const permaModules: PermaModule[] = [
	{
		...dimensions[0],
		description: "培养发现和放大积极体验的能力。",
		skill: "感知美好",
		exercises: [
			"写下今天一个让你开心的小瞬间",
			"给今天的心情拍一张照片",
			"花30秒寻找周围三个让你舒服的东西",
			"回忆最近一次大笑，写一句发生了什么",
			"认真吃第一口食物，不看手机",
			"给今天发生的事起一个有趣标题",
			"找到一个值得感谢的人或事",
			"听一首让自己感觉变好的歌",
			"对镜子里的自己笑5秒",
			"睡前记录今天最好的时刻",
		],
		levels: [
			{ name: "Level 1 发现瞬间", items: ["开心小瞬间", "舒服的东西", "最好时刻"] },
			{ name: "Level 2 放大体验", items: ["心情照片", "一首歌", "有趣标题"] },
			{ name: "Level 3 主动创造", items: ["感谢的人或事", "对自己笑", "认真吃第一口"] },
		],
	},
	{
		...dimensions[1],
		description: "培养专注和进入心流的能力。",
		skill: "保持专注",
		exercises: [
			"连续专注1分钟做一件事",
			"阅读一页内容，不切换应用",
			"关闭通知5分钟",
			"做一次深呼吸后开始任务",
			"找出今天最容易投入的事情",
			"专心观察窗外1分钟",
			"用计时器完成3分钟专注冲刺",
			"做一件事时刻意放慢速度",
			"完成一个不被打断的小任务",
			"记录今天最专注的10分钟",
		],
		levels: [
			{ name: "Level 1 进入当下", items: ["深呼吸后开始", "观察窗外", "放慢速度"] },
			{ name: "Level 2 减少打断", items: ["关闭通知", "阅读一页", "1分钟专注"] },
			{ name: "Level 3 心流冲刺", items: ["3分钟冲刺", "不被打断的小任务", "最专注的10分钟"] },
		],
	},
	{
		...dimensions[2],
		description: "培养连接、表达和回应他人的能力。",
		skill: "建立连接",
		exercises: [
			"给一个人发一句问候",
			"真诚夸奖一个人",
			"回复一条拖延很久的消息",
			"认真听别人说完一句话",
			"对服务人员说谢谢",
			"给朋友发一个有趣内容",
			"问别人一个近况问题",
			"对一个人表达感谢",
			"分享今天发生的小事",
			"记住并使用对方的名字",
		],
		levels: [
			{ name: "Level 1 自我表达", items: ["发一个表情", "发一句问候", "分享一张照片"] },
			{ name: "Level 2 主动连接", items: ["夸奖一个人", "感谢一个人", "询问近况"] },
			{ name: "Level 3 深度连接", items: ["分享真实感受", "倾听5分钟", "表达脆弱或需求"] },
		],
	},
	{
		...dimensions[3],
		description: "培养把日常行为与价值连接的能力。",
		skill: "发现价值",
		exercises: [
			"写一句“我为什么做这件事”",
			"帮别人完成一个小忙",
			"整理一个公共空间",
			"捡起地上的垃圾",
			"给未来的自己留一句话",
			"写下今天影响了谁",
			"记录一个值得坚持的原因",
			"发现今天行为背后的价值",
			"做一件没人要求但有益的事",
			"想想今天什么事值得被记住",
		],
		levels: [
			{ name: "Level 1 看见原因", items: ["为什么做这件事", "值得坚持的原因", "未来留言"] },
			{ name: "Level 2 连接他人", items: ["帮一个小忙", "今天影响了谁", "公共空间"] },
			{ name: "Level 3 承担价值", items: ["没人要求但有益", "行为背后的价值", "值得被记住"] },
		],
	},
	{
		...dimensions[4],
		description: "培养目标设定和完成闭环的能力。",
		skill: "推动行动",
		exercises: [
			"完成一个2分钟任务",
			"勾掉待办清单上的一项",
			"整理桌面一个角落",
			"学习一个新知识点",
			"记录今天完成的事情",
			"完成10分钟运动",
			"把一个大任务拆成三步",
			"做完一直拖延的小事",
			"连续坚持一个习惯一天",
			"睡前写下今天的一个进步",
		],
		levels: [
			{ name: "Level 1 启动行动", items: ["2分钟任务", "桌面一个角落", "一个新知识点"] },
			{ name: "Level 2 完成闭环", items: ["勾掉一项待办", "拆成三步", "拖延小事"] },
			{ name: "Level 3 积累进步", items: ["10分钟运动", "坚持一天", "睡前写进步"] },
		],
	},
];

const fallbackDrawResult = buildDrawResult(getLocalDateKey(), 2, 7);
function getLocalDateKey(date = new Date()) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
}

function buildDrawResult(date: string, moduleIndex: number, exerciseIndex: number): DrawResult {
	const safeModuleIndex = clampIndex(moduleIndex, permaModules.length);
	const module = permaModules[safeModuleIndex];
	const safeExerciseIndex = clampIndex(exerciseIndex, module.exercises.length);
	const levelIndex = Math.min(module.levels.length - 1, Math.floor(safeExerciseIndex / 4));

	return {
		date,
		exercise: module.exercises[safeExerciseIndex],
		exerciseIndex: safeExerciseIndex,
		level: module.levels[levelIndex],
		module,
		moduleIndex: safeModuleIndex,
	};
}

function clampIndex(index: number, length: number) {
	if (!Number.isInteger(index) || length <= 0) return 0;
	return Math.min(Math.max(index, 0), length - 1);
}

function createRandomDraw() {
	if (typeof window === "undefined") return fallbackDrawResult;

	const today = getLocalDateKey();
	const moduleIndex = randomIndex(permaModules.length);
	const module = permaModules[moduleIndex];
	const exerciseIndex = randomIndex(module.exercises.length);

	return buildDrawResult(today, moduleIndex, exerciseIndex);
}

function randomIndex(length: number) {
	if (length <= 0) return 0;

	if (typeof window !== "undefined" && window.crypto) {
		const values = new Uint32Array(1);
		window.crypto.getRandomValues(values);
		return values[0] % length;
	}

	return Math.floor(Math.random() * length);
}

type CommunityPost = {
	body: string;
	comments: string[];
	dimensionKey: string;
	id: string;
	image?: {
		alt: string;
		caption: string;
		src: string;
	};
	likes: number;
	time: string;
	title: string;
	user: string;
};

const communityPosts: CommunityPost[] = [
	{
		body: "今天终于把感谢说出口了。对方说那天只是顺手帮忙，但我说出来以后，心里像放下了一点东西。",
		comments: [
			"我也很怕表达感谢，但说出来之后关系真的会近一点。",
			"这条让我想起一个也该谢谢的人。",
		],
		dimensionKey: "R",
		id: "thanks",
		image: {
			src: "/journey/gratitude-message.svg",
			alt: "一段写给朋友的感谢消息",
			caption: "发出前留下的感谢记录",
		},
		likes: 42,
		time: "刚刚",
		title: "对一个人表达感谢",
		user: "匿名练习者 27",
	},
	{
		body: "关掉通知做了 3 分钟专注冲刺，时间不长，但这 3 分钟里我没有逃走。",
		comments: ["3 分钟很适合开始，我也准备今天试一次。"],
		dimensionKey: "E",
		id: "focus",
		image: {
			src: "/journey/window-listening.svg",
			alt: "窗边安静专注的时刻",
			caption: "关掉通知后，给自己 3 分钟",
		},
		likes: 31,
		time: "2 分钟前",
		title: "关闭通知做了3分钟专注冲刺",
		user: "海盐练习者",
	},
	{
		body: "写下今天最好的时刻：下班路上风很舒服，耳机里刚好放到喜欢的歌。",
		comments: ["这个画面很轻，但很有力量。", "我今天最好的时刻是早餐第一口。"],
		dimensionKey: "P",
		id: "moment",
		image: {
			src: "/journey/evening-note.svg",
			alt: "下班路上拍下的傍晚天空",
			caption: "今天最好的时刻",
		},
		likes: 58,
		time: "5 分钟前",
		title: "记录今天最好的时刻",
		user: "匿名练习者 08",
	},
	{
		body: "完成了一个拖了很久的 2 分钟任务。它本身不大，真正难的是开始。",
		comments: ["开始就是最难的部分，已经很棒了。"],
		dimensionKey: "A",
		id: "start",
		likes: 26,
		time: "8 分钟前",
		title: "完成一个2分钟任务",
		user: "午后三点",
	},
];

const anonymousActions = [
	{ dimensionKey: "R", id: "thanks-now", text: "有人对一个人表达了感谢", time: "刚刚" },
	{ dimensionKey: "A", id: "tiny-task", text: "有人完成了一个2分钟任务", time: "2 分钟前" },
	{ dimensionKey: "P", id: "best-moment", text: "有人记录了今天最好的时刻", time: "5 分钟前" },
	{
		dimensionKey: "E",
		id: "focus-sprint",
		text: "有人关闭通知做了3分钟专注冲刺",
		time: "8 分钟前",
	},
	{ dimensionKey: "M", id: "daily-value", text: "有人写下了今天行为背后的价值", time: "12 分钟前" },
	{ dimensionKey: "R", id: "hello", text: "有人给朋友发了一句问候", time: "16 分钟前" },
	{ dimensionKey: "P", id: "music", text: "有人听了一首让自己变好的歌", time: "21 分钟前" },
	{
		dimensionKey: "E",
		id: "read-page",
		text: "有人阅读了一页内容，没有切换应用",
		time: "25 分钟前",
	},
	{ dimensionKey: "A", id: "desk", text: "有人整理了桌面的一个角落", time: "31 分钟前" },
	{ dimensionKey: "M", id: "future-note", text: "有人给未来的自己留了一句话", time: "36 分钟前" },
];

const currentUser = {
	avatar: "张",
	nickname: "张三",
	path: "R Level 2 · 主动连接",
	streak: "连续 7 天训练",
};

type TimelineItem = {
	action: string;
	feeling: string;
	image?: {
		alt: string;
		caption: string;
		src: string;
	};
	meaning: string;
	time: string;
	title: string;
};

const timelineItems: TimelineItem[] = [
	{
		title: "对一个人表达感谢",
		time: "2026-06-13 21:34:07",
		action: "我给一直支持我的朋友发了一段消息，具体谢谢他上次帮我搬家那一整天。",
		feeling: "写的时候有点不好意思，但发出去之后心里特别暖。",
		meaning: "我其实一直被很多人默默支持着，只是很少说出口。",
		image: {
			src: "/journey/gratitude-message.svg",
			alt: "一段发给朋友的感谢消息",
			caption: "消息发出后的截图记录",
		},
	},
	{
		title: "给今天的心情拍一张照片",
		time: "2026-06-12 20:18:22",
		action: "回家路上看到天色很好，停下来拍了一张照片，也把那一刻的轻松记了下来。",
		feeling: "那几分钟没有急着赶路，感觉自己真的从一天里抽身出来了。",
		meaning: "积极情绪不是等它出现，而是主动停下来把它看见。",
		image: {
			src: "/journey/evening-note.svg",
			alt: "睡前记录今天心情的笔记",
			caption: "今天心情照片旁边的简短记录",
		},
	},
	{
		title: "问别人一个近况问题",
		time: "2026-06-10 08:12:45",
		action: "我主动联系了久未联系的老同学，问他最近怎么样，约了周末一起吃饭。",
		feeling: "按下发送前犹豫了很久，收到回复后特别开心。",
		meaning: "关系是需要主动维护的，主动一点并不可怕。",
		image: {
			src: "/journey/weekend-meal.svg",
			alt: "和老同学约周末吃饭的记录",
			caption: "约好周末见面的饭局记录",
		},
	},
	{
		title: "认真听别人说完一句话",
		time: "2026-06-07 22:05:18",
		action: "同事跟我倾诉烦恼时，我认真听他说完，没有打断也没有急着给建议。",
		feeling: "一开始很想插话，忍住之后反而更专注了。",
		meaning: "有时候陪伴比解决问题更重要。",
		image: {
			src: "/journey/window-listening.svg",
			alt: "认真倾听同事倾诉的时刻",
			caption: "一次没有打断的倾听练习",
		},
	},
	{
		title: "分享今天发生的小事",
		time: "2026-06-04 19:47:33",
		action: "我跟家人说了今天工作里一个小插曲，也说了自己最近有点压力。",
		feeling: "说出口前担心被误解，说完之后轻松了很多。",
		meaning: "真实分享让关系更近，也让我不用一个人扛着。",
	},
	{
		title: "给一个人发一句问候",
		time: "2026-06-01 12:53:09",
		action: "我给朋友发了一句“最近还好吗”，没有附带任何请求。",
		feeling: "很小的一步，但发出去之后觉得关系没有那么远。",
		meaning: "连接不一定要很深，先打开一个入口就够了。",
	},
];

const tabScreens: Screen[] = ["today", "journey", "wall", "report"];
const swipeBackEdgeRatio = 0.18;
const swipeBackMaxEdge = 64;
const swipeBackThreshold = 58;
const swipeBackVerticalTolerance = 70;

function resetSwipeElement(element: HTMLElement) {
	element.classList.remove("is-swiping-back");
}

function isSwipeBlocked(target: EventTarget | null) {
	if (!(target instanceof Element)) return false;

	return Boolean(
		target.closest(
			'button, a, input, textarea, select, label, [role="button"], [contenteditable="true"]',
		),
	);
}

export const Home = () => {
	const [screenStack, setScreenStack] = useState<Screen[]>(["today"]);
	const [drawResult, setDrawResult] = useState<DrawResult>(fallbackDrawResult);
	const contentRef = useRef<HTMLDivElement>(null);
	const swipeRef = useRef<SwipeState | null>(null);
	const screen = screenStack[screenStack.length - 1] ?? "today";
	const canGoBack = screenStack.length > 1;
	const dark = screen === "drawing" || screen === "reveal";

	const navigateTo = useCallback((next: Screen, mode: NavigationMode = "push") => {
		setScreenStack((currentStack) => {
			const current = currentStack[currentStack.length - 1] ?? "today";

			if (mode === "reset") return [next];
			if (current === next) return currentStack;
			if (mode === "replace") return [...currentStack.slice(0, -1), next];

			return [...currentStack, next];
		});
	}, []);

	const goBack = useCallback(() => {
		setScreenStack((currentStack) => {
			if (currentStack.length <= 1) return currentStack;

			return currentStack.slice(0, -1);
		});
	}, []);

	const startDailyDraw = useCallback(() => {
		setDrawResult(createRandomDraw());
		navigateTo("drawing");
	}, [navigateTo]);

	const resetSwipeSurface = useCallback(() => {
		const element = contentRef.current;
		if (!element) return;

		element.classList.remove("is-swiping-back");
		element.classList.add("swipe-back-release");
		window.setTimeout(() => element.classList.remove("swipe-back-release"), 180);
	}, []);

	const handlePointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (!canGoBack || (event.pointerType === "mouse" && event.button !== 0)) return;
			if (isSwipeBlocked(event.target)) return;

			const rect = event.currentTarget.getBoundingClientRect();
			const edgeSize = Math.min(swipeBackMaxEdge, rect.width * swipeBackEdgeRatio);
			const fromLeft = event.clientX - rect.left <= edgeSize;
			const fromRight = rect.right - event.clientX <= edgeSize;

			if (!fromLeft && !fromRight) return;

			swipeRef.current = {
				fromLeft,
				pointerId: event.pointerId,
				startX: event.clientX,
				startY: event.clientY,
				tracking: false,
			};
		},
		[canGoBack],
	);

	const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
		const swipe = swipeRef.current;
		const element = contentRef.current;
		if (!swipe || !element || swipe.pointerId !== event.pointerId) return;

		const deltaX = event.clientX - swipe.startX;
		const deltaY = event.clientY - swipe.startY;
		const inwardDistance = swipe.fromLeft ? deltaX : -deltaX;

		if (Math.abs(deltaY) > swipeBackVerticalTolerance && Math.abs(deltaY) > Math.abs(deltaX)) {
			swipeRef.current = null;
			resetSwipeElement(element);
			return;
		}

		if (inwardDistance <= 0 || Math.abs(deltaX) < 8) return;

		event.preventDefault();
		swipe.tracking = true;
		element.classList.add("is-swiping-back");
	}, []);

	const handlePointerUp = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			const swipe = swipeRef.current;
			if (!swipe || swipe.pointerId !== event.pointerId) return;

			const deltaX = event.clientX - swipe.startX;
			const deltaY = event.clientY - swipe.startY;
			const inwardDistance = swipe.fromLeft ? deltaX : -deltaX;
			const shouldGoBack =
				swipe.tracking &&
				inwardDistance >= swipeBackThreshold &&
				Math.abs(deltaY) <= swipeBackVerticalTolerance;

			swipeRef.current = null;
			resetSwipeSurface();

			if (shouldGoBack) {
				goBack();
			}
		},
		[goBack, resetSwipeSurface],
	);

	const handlePointerCancel = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (swipeRef.current?.pointerId !== event.pointerId) return;

			swipeRef.current = null;
			resetSwipeSurface();
		},
		[resetSwipeSurface],
	);

	const content = useMemo(() => {
		switch (screen) {
			case "drawing":
				return <DrawScreen onBack={goBack} onReveal={() => navigateTo("reveal", "replace")} />;
			case "reveal":
				return (
					<RevealScreen onBack={goBack} onStart={() => navigateTo("task")} result={drawResult} />
				);
			case "task":
				return (
					<TaskScreen onBack={goBack} onStart={() => navigateTo("submit")} result={drawResult} />
				);
			case "submit":
				return (
					<SubmitScreen
						onBack={goBack}
						onSubmit={() => navigateTo("evidence")}
						result={drawResult}
					/>
				);
			case "evidence":
				return (
					<EvidenceScreen
						onBack={goBack}
						onJourney={() => navigateTo("journey")}
						onToday={() => navigateTo("today", "reset")}
						result={drawResult}
					/>
				);
			case "journey":
				return <JourneyScreen />;
			case "report":
				return <ReportScreen />;
			case "wall":
				return <WallScreen />;
			case "today":
			default:
				return <TodayScreen onDraw={startDailyDraw} onEvidence={() => navigateTo("evidence")} />;
		}
	}, [drawResult, goBack, navigateTo, screen, startDailyDraw]);
	const showTab = tabScreens.includes(screen);

	return (
		<main className="web2-stage">
			<div className={`phone-frame${dark ? " is-dark" : ""}`}>
				<div
					className={`phone-content screen-${screen}${showTab ? " with-tab" : ""}`}
					onPointerCancel={handlePointerCancel}
					onPointerDown={handlePointerDown}
					onPointerLeave={handlePointerCancel}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					ref={contentRef}
				>
					{content}
				</div>
				{showTab ? <TabBar active={screen} onChange={(next) => navigateTo(next, "reset")} /> : null}
			</div>
		</main>
	);
};

function TabBar({ active, onChange }: { active: Screen; onChange: (screen: Screen) => void }) {
	const tabs: Array<{ id: Screen; label: string; icon: LucideIcon }> = [
		{ id: "today", label: "今日", icon: Flower2 },
		{ id: "journey", label: "历程", icon: BookmarkCheck },
		{ id: "wall", label: "进展墙", icon: Users },
		{ id: "report", label: "报告", icon: BarChart3 },
	];

	return (
		<nav className="tab-shell" aria-label="主导航">
			<div className="tab-bar">
				{tabs.map((tab) => {
					const Icon = tab.icon;
					const current = active === tab.id;
					return (
						<button
							type="button"
							key={tab.id}
							className={`tab-item${current ? " active" : ""}`}
							onClick={() => onChange(tab.id)}
						>
							<Icon className="tab-icon" />
							<span>{tab.label}</span>
						</button>
					);
				})}
			</div>
		</nav>
	);
}

function TodayScreen({ onDraw, onEvidence }: { onDraw: () => void; onEvidence: () => void }) {
	return (
		<section className="screen-stack today-screen">
			<HeaderBlock eyebrow="CURRENT CYCLE" title="今日训练" subtitle="7 天积极生活训练 · 第 3 天" />
			<div className="perma-strip">
				<p>PERMA 五维成长</p>
				<div className="perma-pills">
					{dimensions.map((dimension) => (
						<div className="perma-pill" key={dimension.key} style={dimensionStyle(dimension)}>
							<span>{dimension.key}</span>
							<small>{dimension.shortName}</small>
						</div>
					))}
				</div>
			</div>

			<button type="button" className="draw-card" onClick={onDraw}>
				<div>
					<strong>抽取今日训练</strong>
					<span>每次都会随机抽取一个训练模块</span>
				</div>
				<div className="draw-icon">
					<Shuffle />
				</div>
			</button>

			<div className="section-label">最近一次训练证据</div>
			<EvidenceCard compact onClick={onEvidence} />
		</section>
	);
}

function DrawScreen({ onBack, onReveal }: { onBack: () => void; onReveal: () => void }) {
	const [phase, setPhase] = useState<"shuffle" | "lock">("shuffle");

	useEffect(() => {
		const lockTimer = window.setTimeout(() => setPhase("lock"), 1900);
		const revealTimer = window.setTimeout(onReveal, 2850);

		return () => {
			window.clearTimeout(lockTimer);
			window.clearTimeout(revealTimer);
		};
	}, [onReveal]);

	const isLocking = phase === "lock";

	return (
		<section className={`screen-stack draw-screen draw-${phase}`}>
			<TopBar dark onBack={onBack} />
			<div className="dark-heading">
				<span>DAILY DRAW</span>
				<h1>今日 PERMA 训练</h1>
				<p>{isLocking ? "正在为你锁定今日练习" : "五个方向正在随机轮转"}</p>
			</div>
			<div className="draw-stage">
				<div className="card-fan">
					{["P", "E", "A", "M", "R"].map((letter, index) => (
						<div className={`draw-tile tile-${index}`} key={letter}>
							{letter}
						</div>
					))}
				</div>
				<div className="question-card">
					<strong>?</strong>
					<span>P E R M A</span>
				</div>
			</div>
			<div className="draw-footer draw-loading-footer" aria-live="polite">
				<div className="draw-status">
					<p>{isLocking ? "已抽中，准备揭晓" : "正在随机抽取今日训练..."}</p>
					<span>{isLocking ? "结果已锁定 · 即将打开" : "纯随机 · 不可预测"}</span>
				</div>
				<div className="draw-progress" aria-hidden="true">
					<i />
					<i />
					<i />
				</div>
			</div>
		</section>
	);
}

function RevealScreen({
	onBack,
	onStart,
	result,
}: {
	onBack: () => void;
	onStart: () => void;
	result: DrawResult;
}) {
	return (
		<section className="screen-stack draw-screen reveal-screen">
			<TopBar dark onBack={onBack} />
			<div className="dark-heading">
				<span>DAILY DRAW</span>
				<h1>抽取完成</h1>
				<p>今天的练习方向已经为你落定</p>
			</div>
			<div className="reveal-stage">
				<div className="reveal-orbit" aria-label="PERMA 五维">
					{permaModules.map((module) => (
						<span className={module.key === result.module.key ? "active" : ""} key={module.key}>
							{module.key}
						</span>
					))}
				</div>
				<div className="reveal-result" style={dimensionStyle(result.module)}>
					<div className="reveal-card-face">
						<strong>{result.module.key}</strong>
						<span>{result.module.name}</span>
						<small>{result.module.skill}</small>
					</div>
				</div>
			</div>
			<div className="draw-footer">
				<div className="reveal-target">
					<span>今日目标</span>
					<p>{result.exercise}</p>
				</div>
				<small>纯随机抽取 · 每次重新生成</small>
				<button type="button" className="dark-action" onClick={onStart}>
					开始练习
					<ArrowRight />
				</button>
			</div>
		</section>
	);
}

function TaskScreen({
	onBack,
	onStart,
	result,
}: {
	onBack: () => void;
	onStart: () => void;
	result: DrawResult;
}) {
	return (
		<section className="screen-stack task-screen">
			<TopBar eyebrow={`${result.module.key} ${result.module.name}`} onBack={onBack} />
			<HeaderBlock
				eyebrow={`${result.level.name} · ${result.module.key} ${result.module.name}`}
				title={result.exercise}
			/>
			<div className="task-intro">
				<div className="task-meta">
					<span>练习定位 · PERMA</span>
					<strong>{result.module.skill}</strong>
				</div>
				<div className="perma-axis">
					{dimensions.map((dimension) => (
						<span
							key={dimension.key}
							className={dimension.key === result.module.key ? "selected" : ""}
							style={dimensionStyle(dimension)}
						>
							{dimension.key}
						</span>
					))}
				</div>
				<p>
					{result.module.description} 这不是一次孤立打卡，而是在逐步习得「{result.module.skill}」。
				</p>
			</div>
			<div className="effect-card">
				<span>这个练习的作用</span>
				<p>
					把「{result.module.skill}」从抽象目标变成一个今天能完成的小动作：{result.exercise}。
				</p>
			</div>
			<InfoCard label="练习说明">
				<p>用 2 到 5 分钟完成这个动作。动作越小越好，重点是今天真实发生一次。</p>
			</InfoCard>
			<InfoCard label="为什么有效" accent>
				<p>轻训练通过低门槛、可完成、可反馈的行为，逐步建立 PERMA 对应的心理技能。</p>
			</InfoCard>
			<div className="level-card">
				<div className="section-label">{result.module.name}成长路径</div>
				{result.module.levels.map((level, index) => (
					<div
						className={level === result.level ? "level-row current" : "level-row"}
						key={level.name}
					>
						<strong>{level.name}</strong>
						<span>{level.items.join(" · ")}</span>
						<small>{index + 1}</small>
					</div>
				))}
			</div>
			<div className="requirement-card">
				<div className="section-label">完成要求</div>
				{["完成今天抽到的动作", "用一句话记录你实际做了什么", "感受、发现和评分都可以留空"].map(
					(item) => (
						<div className="requirement-row" key={item}>
							<Circle />
							<span>{item}</span>
						</div>
					),
				)}
			</div>
			<button type="button" className="primary-action" onClick={onStart}>
				开始记录
				<ArrowRight />
			</button>
		</section>
	);
}

function SubmitScreen({
	onBack,
	onSubmit,
	result,
}: {
	onBack: () => void;
	onSubmit: () => void;
	result: DrawResult;
}) {
	const [actionText, setActionText] = useState("");
	const [feelingText, setFeelingText] = useState("");
	const [meaningText, setMeaningText] = useState("");
	const canSubmit = actionText.trim().length > 0;
	const actionChips = [
		`我完成了「${result.exercise}」`,
		"我做了一个很小的版本",
		"我开始做了，但还没完全完成",
	];

	return (
		<section className="screen-stack submit-screen">
			<TopBar eyebrow={`${result.module.key} ${result.module.name}`} onBack={onBack} />
			<HeaderBlock eyebrow={result.exercise} title="提交今日训练" />
			<div className="form-card quick-record-card">
				<LabeledTextarea
					badge="必填"
					helper="比如：我整理了桌面左上角，把两张纸归档。"
					label="我做了什么？"
					onChange={setActionText}
					placeholder="记录你的具体行动..."
					value={actionText}
				/>
				<QuickChips
					items={actionChips}
					label="快速填写具体行动"
					onSelect={(item) => setActionText(item)}
				/>
				<LabeledTextarea
					badge="可选"
					label="我有什么感受？"
					onChange={setFeelingText}
					placeholder="此刻的情绪、身体感受，或者留空..."
					rows={2}
					value={feelingText}
				/>
				<LabeledTextarea
					badge="可选"
					label="这件事说明了什么？"
					onChange={setMeaningText}
					placeholder="你从中看见了什么，或者留空..."
					rows={2}
					value={meaningText}
				/>
				<div className="upload-row">
					<div className="section-label">图片 · 可选</div>
					<div className="upload-options">
						<UploadPill icon={ImageIcon} label="图片" />
					</div>
				</div>
			</div>
			<div className="submit-actions">
				<button type="button" className="primary-action" disabled={!canSubmit} onClick={onSubmit}>
					{canSubmit ? "生成证据卡" : "写一句行动后生成"}
					<ArrowRight />
				</button>
				<span>只有“我做了什么”是必填。</span>
			</div>
		</section>
	);
}

function EvidenceScreen({
	onBack,
	onJourney,
	onToday,
	result,
}: {
	onBack: () => void;
	onJourney: () => void;
	onToday: () => void;
	result: DrawResult;
}) {
	return (
		<section className="screen-stack evidence-screen">
			<TopBar onBack={onBack} trailing={<SavedBadge />} />
			<HeaderBlock
				eyebrow="TODAY EVIDENCE"
				title={`今日${result.module.name}证据`}
				subtitle="你今天为自己的人生历程，留下了一笔。"
			/>
			<EvidenceCard result={result} />
			<div className="action-grid">
				<button type="button" className="secondary-action" onClick={onToday}>
					回到今日
				</button>
				<button type="button" className="primary-action compact" onClick={onJourney}>
					查看历程
					<ArrowRight />
				</button>
			</div>
		</section>
	);
}

function JourneyScreen() {
	return (
		<section className="screen-stack journey-screen">
			<HeaderBlock eyebrow="MY JOURNEY" title="我的人生历程" subtitle="所有训练证据按时间排列" />
			<div className="timeline-heading">
				<span>训练时间轴</span>
				<small>{timelineItems.length} 条记录</small>
			</div>
			<div className="timeline-list">
				{timelineItems.map((item) => (
					<TimelineEntry key={`${item.title}-${item.time}`} item={item} />
				))}
			</div>
		</section>
	);
}

function ReportScreen() {
	return (
		<section className="screen-stack report-screen">
			<HeaderBlock
				eyebrow="GROWTH REPORT · 全部历程"
				title={`${currentUser.nickname}的成长报告`}
				subtitle="基于 18 次 PERMA 轻训练证据生成"
			/>
			<div className="report-user-card">
				<div className="report-avatar">{currentUser.avatar}</div>
				<div>
					<span>用户昵称</span>
					<strong>{currentUser.nickname}</strong>
				</div>
				<div className="report-user-meta">
					<small>{currentUser.streak}</small>
					<small>{currentUser.path}</small>
				</div>
			</div>
			<div className="stats-grid">
				<StatCard icon={Trophy} label="累计训练" unit="次" value="18" />
				<StatCard icon={Clock3} label="活跃天数" unit="天" value="14" />
				<StatCard icon={Zap} label="当前路径" unit="R Level 2" value="2" />
			</div>
			<div className="distribution-card">
				<div className="section-label">五维练习分布 · 各练了多少次</div>
				{dimensions.map((dimension) => (
					<DistributionRow dimension={dimension} key={dimension.key} />
				))}
				<p>最常练 A 成就感，最少练 M 意义感 —— 下周可以补一点“发现价值”的练习。</p>
			</div>
		</section>
	);
}

function WallScreen() {
	const [likedPostIds, setLikedPostIds] = useState<Set<string>>(() => new Set());
	const [openCommentsId, setOpenCommentsId] = useState(communityPosts[0]?.id ?? "");
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const [feedFilter, setFeedFilter] = useState<"all" | "same" | "commented">("all");
	const [localComments, setLocalComments] = useState<Record<string, string[]>>({});

	const commentCount = (post: (typeof communityPosts)[number]) =>
		post.comments.length + (localComments[post.id]?.length ?? 0);
	const visiblePosts = communityPosts
		.filter((post) => feedFilter !== "same" || post.dimensionKey === "R")
		.sort((first, second) =>
			feedFilter === "commented" ? commentCount(second) - commentCount(first) : 0,
		);

	const toggleLike = (postId: string) => {
		setLikedPostIds((current) => {
			const next = new Set(current);
			if (next.has(postId)) {
				next.delete(postId);
			} else {
				next.add(postId);
			}

			return next;
		});
	};

	const sendComment = (postId: string) => {
		const text = drafts[postId]?.trim();
		if (!text) return;

		setLocalComments((current) => ({
			...current,
			[postId]: [...(current[postId] ?? []), text],
		}));
		setDrafts((current) => ({
			...current,
			[postId]: "",
		}));
		setOpenCommentsId(postId);
	};

	return (
		<section className="screen-stack wall-screen">
			<HeaderBlock
				eyebrow="TODAY · COMMUNITY"
				title="共同进展墙"
				subtitle="看看今天大家完成了什么，也给彼此一点回应。"
			/>
			<div className="community-card">
				<div className="community-top">
					<Users />
					<span>今日社区</span>
				</div>
				<strong>1,284 人</strong>
				<p>今天已完成积极生活训练，大家正在用很小的行动互相陪伴。</p>
				<div className="community-pulse">
					<span>326 次鼓励</span>
					<span>94 条评论</span>
					<span>18 条新动态</span>
				</div>
			</div>
			<AutoActionStream />
			<div className="community-filter" aria-label="动态筛选">
				<button
					type="button"
					className={feedFilter === "all" ? "is-active" : ""}
					onClick={() => setFeedFilter("all")}
				>
					全部动态
				</button>
				<button
					type="button"
					className={feedFilter === "same" ? "is-active" : ""}
					onClick={() => setFeedFilter("same")}
				>
					同练习
				</button>
				<button
					type="button"
					className={feedFilter === "commented" ? "is-active" : ""}
					onClick={() => setFeedFilter("commented")}
				>
					最新评论
				</button>
			</div>
			<div className="social-feed">
				<div className="feed-heading">
					<span>社区动态</span>
					<small>匿名展示 · 不排名</small>
				</div>
				{visiblePosts.map((post) => {
					const dimension = dimensions.find((item) => item.key === post.dimensionKey);
					const isLiked = likedPostIds.has(post.id);
					const allComments = [...post.comments, ...(localComments[post.id] ?? [])];
					const isOpen = openCommentsId === post.id;

					return (
						<article className="social-post" key={post.id} style={dimensionStyle(dimension)}>
							<div className="post-head">
								<div className="post-avatar" aria-hidden="true">
									{post.dimensionKey}
								</div>
								<div className="post-user">
									<strong>{post.user}</strong>
									<span>{post.time}</span>
								</div>
								<span className="post-badge">
									{post.dimensionKey} {dimension?.name}
								</span>
							</div>
							<h2>{post.title}</h2>
							<p>{post.body}</p>
							{post.image ? (
								<figure className="post-image">
									<img src={post.image.src} alt={post.image.alt} />
									<figcaption>{post.image.caption}</figcaption>
								</figure>
							) : null}
							<div className="post-actions">
								<button
									type="button"
									className={`post-action${isLiked ? " is-liked" : ""}`}
									aria-label={`给${post.title}点赞`}
									onClick={() => toggleLike(post.id)}
								>
									<Heart fill={isLiked ? "currentColor" : "none"} />
									{post.likes + (isLiked ? 1 : 0)}
								</button>
								<button
									type="button"
									className="post-action"
									aria-label={`查看${post.title}评论`}
									onClick={() => setOpenCommentsId(isOpen ? "" : post.id)}
								>
									<MessageCircle />
									{allComments.length}
								</button>
							</div>
							{isOpen ? (
								<div className="comment-list">
									{allComments.map((comment, index) => (
										<div className="comment-row" key={`${post.id}-${comment}-${index.toString()}`}>
											<span>{index >= post.comments.length ? "我" : "回"}</span>
											<p>{comment}</p>
										</div>
									))}
									<div className="comment-composer">
										<input
											aria-label={`评论 ${post.title}`}
											onChange={(event) =>
												setDrafts((current) => ({
													...current,
													[post.id]: event.target.value,
												}))
											}
											placeholder="写一句鼓励或回应"
											value={drafts[post.id] ?? ""}
										/>
										<button
											type="button"
											aria-label="发送评论"
											disabled={!drafts[post.id]?.trim()}
											onClick={() => sendComment(post.id)}
										>
											<Send />
										</button>
									</div>
								</div>
							) : null}
						</article>
					);
				})}
			</div>
		</section>
	);
}

function AutoActionStream() {
	const streamItems = [...anonymousActions, ...anonymousActions];

	return (
		<section className="auto-stream-card" aria-label="匿名行动流">
			<div className="stream-heading">
				<span>匿名行动流</span>
				<small>自动更新</small>
			</div>
			<div className="auto-stream-window">
				<div className="auto-stream-track">
					{streamItems.map((item, index) => {
						const dimension = dimensions.find(
							(dimensionItem) => dimensionItem.key === item.dimensionKey,
						);

						return (
							<div
								aria-hidden={index >= anonymousActions.length}
								className="auto-stream-row"
								key={`${item.id}-${index.toString()}`}
								style={dimensionStyle(dimension)}
							>
								<Heart />
								<div>
									<p>{item.text}</p>
									<span>{item.time}</span>
								</div>
							</div>
						);
					})}
				</div>
			</div>
			<small className="stream-footnote">不显示排名 · 只展示匿名进展</small>
		</section>
	);
}

function HeaderBlock({
	eyebrow,
	subtitle,
	title,
}: {
	eyebrow: string;
	subtitle?: string;
	title: string;
}) {
	return (
		<header className="header-block">
			<span>{eyebrow}</span>
			<h1>{title}</h1>
			{subtitle ? <p>{subtitle}</p> : null}
		</header>
	);
}

function TopBar({
	dark,
	eyebrow,
	onBack,
	trailing,
}: {
	dark?: boolean;
	eyebrow?: string;
	onBack: () => void;
	trailing?: ReactNode;
}) {
	return (
		<div className={`top-bar${dark ? " top-dark" : ""}`}>
			<button type="button" onClick={onBack} aria-label="返回">
				<ArrowLeft />
			</button>
			{eyebrow ? <span>{eyebrow}</span> : <span />}
			{trailing ?? <span />}
		</div>
	);
}

function InfoCard({
	accent,
	children,
	label,
}: {
	accent?: boolean;
	children: ReactNode;
	label: string;
}) {
	return (
		<div className={`info-card${accent ? " accent" : ""}`}>
			<span>{label}</span>
			{children}
		</div>
	);
}

function LabeledTextarea({
	badge,
	helper,
	label,
	onChange,
	placeholder,
	rows = 3,
	value,
}: {
	badge?: string;
	helper?: string;
	label: string;
	onChange: (value: string) => void;
	placeholder: string;
	rows?: number;
	value: string;
}) {
	return (
		<label className="textarea-field">
			<div className="field-head">
				<span>{label}</span>
				{badge ? <small>{badge}</small> : null}
			</div>
			<textarea
				onChange={(event) => onChange(event.target.value)}
				placeholder={placeholder}
				rows={rows}
				value={value}
			/>
			{helper ? <em>{helper}</em> : null}
		</label>
	);
}

function QuickChips({
	items,
	label,
	onSelect,
}: {
	items: string[];
	label: string;
	onSelect: (item: string) => void;
}) {
	return (
		<div className="quick-chip-row" aria-label={label}>
			{items.map((item) => (
				<button className="quick-chip" key={item} onClick={() => onSelect(item)} type="button">
					{item}
				</button>
			))}
		</div>
	);
}

function UploadPill({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
	return (
		<button type="button" className="upload-pill">
			<Icon />
			<span>{label}</span>
		</button>
	);
}

function EvidenceCard({
	compact,
	onClick,
	result,
}: {
	compact?: boolean;
	onClick?: () => void;
	result?: DrawResult;
}) {
	const tag = result ? `${result.module.key} ${result.module.name}` : "R 关系连接";
	const title = result ? `我完成了：${result.exercise}` : "我向朋友表达了一次具体感谢";
	const record = result
		? `今天抽到「${result.exercise}」。我先记录了实际行动，这条证据会进入「${result.module.skill}」路径，感受和发现可以之后再补。`
		: "我谢谢了一直默默支持我的朋友，他说他也很开心，原来我们都把这段关系放在心上。";

	const card = (
		<article className={`evidence-card${compact ? " compact" : ""}`}>
			<div className="evidence-top">
				<span>{tag}</span>
				<small>06·13</small>
			</div>
			<h2>{title}</h2>
			<div className="my-record">
				<span>我的记录</span>
				<p>{record}</p>
			</div>
		</article>
	);

	if (!onClick) return card;

	return (
		<button type="button" className="evidence-button" onClick={onClick}>
			{card}
		</button>
	);
}

function SavedBadge() {
	return (
		<div className="saved-badge">
			<BookmarkCheck />
			<span>已收藏</span>
		</div>
	);
}

function StatCard({
	icon: Icon,
	label,
	unit,
	value,
}: {
	icon: LucideIcon;
	label: string;
	unit: string;
	value: string;
}) {
	return (
		<div className="stat-card">
			<Icon />
			<span>{label}</span>
			<strong>{value}</strong>
			<small>{unit}</small>
		</div>
	);
}

function DistributionRow({ dimension }: { dimension: Dimension }) {
	const width = `${Math.max(14, dimension.count * 12)}%`;

	return (
		<div className="distribution-row" style={dimensionStyle(dimension)}>
			<div>
				<span>
					{dimension.key} {dimension.name}
				</span>
				<strong>{dimension.count} 次</strong>
			</div>
			<div className="distribution-track">
				<i style={{ width }} />
			</div>
		</div>
	);
}

function TimelineEntry({ item }: { item: TimelineItem }) {
	const date = formatTimelineDate(item.time);

	return (
		<div className="timeline-entry">
			<div
				className="timeline-axis"
				aria-label={`${date.year}年${date.month}月${date.day}日 ${date.clock}`}
			>
				<div className="timeline-date">
					<strong>{date.day}</strong>
					<span>{date.month}月</span>
				</div>
				<i aria-hidden="true" />
				<small>{date.clock}</small>
			</div>
			<TimelineCard item={item} />
		</div>
	);
}

function TimelineCard({ item }: { item: TimelineItem }) {
	const date = formatTimelineDate(item.time);

	return (
		<article className="timeline-card">
			<div className="timeline-title">
				<div>
					<strong>{item.title}</strong>
					<span>
						{date.year}-{date.month}-{date.day} {date.clock}
					</span>
				</div>
			</div>
			{item.image ? (
				<figure className="timeline-image">
					<img src={item.image.src} alt={item.image.alt} loading="lazy" />
					<figcaption>{item.image.caption}</figcaption>
				</figure>
			) : null}
			<TimelineSection label="我做了什么">{item.action}</TimelineSection>
			<TimelineSection label="我有什么感受">{item.feeling}</TimelineSection>
			<TimelineSection label="这件事说明了什么">{item.meaning}</TimelineSection>
		</article>
	);
}

function formatTimelineDate(timestamp: string) {
	const [date = "", clockWithSeconds = ""] = timestamp.split(" ");
	const [year = "", month = "", day = ""] = date.split("-");
	const clock = clockWithSeconds.slice(0, 5);

	return {
		clock,
		day,
		month,
		year,
	};
}

function TimelineSection({ children, label }: { children: ReactNode; label: string }) {
	return (
		<div className="timeline-section">
			<span>{label}</span>
			<p>{children}</p>
		</div>
	);
}

function dimensionStyle(dimension?: Dimension) {
	return {
		"--dimension-color": dimension?.color ?? "var(--perma-r)",
		"--dimension-soft": dimension?.soft ?? "var(--perma-r-soft)",
	} as CSSProperties;
}
